import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// `index.ts` imports `favoritesContract` from this file, so we can't
// pull `threadListResponseSchema` back in without creating a temporal-
// dead-zone cycle at module-eval time. The Favorites endpoint hands
// back the canonical thread list shape, so we declare the envelope
// here and treat each row as `z.unknown()` — the API handler still
// builds a fully-typed `ThreadListResponse` from `toThreadResource`
// so the wire payload is correct, and `ts-rest` only uses this schema
// for request-side response validation.
const favoriteThreadListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('thread'),
  has_more: z.boolean(),
  objects: z.array(z.unknown()),
});

const errorSchema = z.object({ message: z.string() });

export const toggleThreadFavoriteResponseSchema = z.object({
  thread_id: z.string().min(1),
  is_favorited: z.boolean(),
  favorited_at: z.string().datetime().nullable(),
});

export const listFavoriteThreadsQuerySchema = z.object({
  /** Cursor: a thread id; the response continues from the next favorited row. */
  before: z.string().min(1).max(64).optional(),
});

// Author shape on a favorite-prompt row. Mirrors the slim author shape
// used elsewhere in lists so the row can render an avatar/initial chip
// without an extra fetch.
export const favoritePromptAuthorSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
});

// One favorited prompt — i.e. a single user-message `thread_blocks` row
// the viewer has starred. Carries enough thread context to render a
// meaningful list row and link back to the source turn (`#turn-{id}`)
// inside the parent thread.
export const favoritePromptResourceSchema = z.object({
  /** thread_block id — `tb_…`. Stable per-prompt identifier. */
  block_id: z.string().min(1),
  thread_id: z.string().min(1),
  thread_title: z.string(),
  /** Visibility of the parent thread at read time. */
  thread_visibility: z.enum(['private', 'workspace', 'public']),
  author: favoritePromptAuthorSchema,
  /** Truncated user-prompt text, single-line, suitable for a list row. */
  preview: z.string(),
  /** ISO timestamp the parent block was originally created at. */
  prompted_at: z.string().datetime().nullable(),
  /** ISO timestamp the viewer favorited this prompt. */
  favorited_at: z.string().datetime(),
});

export const favoritePromptListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('favorite_prompt'),
  has_more: z.boolean(),
  objects: z.array(favoritePromptResourceSchema),
});

export const togglePromptFavoriteResponseSchema = z.object({
  block_id: z.string().min(1),
  /** Parent thread id; null on remove when the row didn't exist (the
   * remove endpoint is idempotent, so we have nothing to look up). */
  thread_id: z.string().min(1).nullable(),
  is_favorited: z.boolean(),
  favorited_at: z.string().datetime().nullable(),
});

export const listFavoritePromptsQuerySchema = z.object({
  /** Cursor: a thread_block id; response continues from the next favorited row. */
  before: z.string().min(1).max(64).optional(),
});

export const favoritedBlockIdsResponseSchema = z.object({
  /** Stable order: each entry is a thread_block id favorited by the viewer
   * within the requested thread. The set is small per-thread (capped by
   * the user's prompt count), so we return it in one shot rather than
   * paginating. Empty for unauthenticated callers. */
  block_ids: z.array(z.string().min(1)),
});

export const favoritesContract = c.router({
  addThreadFavorite: {
    method: 'POST',
    path: '/threads/:id/favorite',
    pathParams: z.object({
      id: z.string().min(1).max(64),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      201: toggleThreadFavoriteResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Bookmark a thread for the authenticated user. Idempotent — re-favoriting returns the existing membership.',
  },
  removeThreadFavorite: {
    method: 'DELETE',
    path: '/threads/:id/favorite',
    pathParams: z.object({
      id: z.string().min(1).max(64),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: toggleThreadFavoriteResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Remove a thread bookmark for the authenticated user (no-op if absent).',
  },
  listFavoriteThreads: {
    method: 'GET',
    path: '/favorites/threads',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    query: listFavoriteThreadsQuerySchema,
    responses: {
      200: favoriteThreadListResponseSchema,
      401: errorSchema,
      403: errorSchema,
    },
    summary:
      "List threads the authenticated user has favorited, newest favorited first. Visibility filtering still applies — a thread the viewer can no longer see (e.g. visibility downgraded) is omitted from the list.",
  },
  addPromptFavorite: {
    method: 'POST',
    path: '/thread-blocks/:id/favorite',
    pathParams: z.object({
      id: z.string().min(1).max(64),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      201: togglePromptFavoriteResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
      409: errorSchema,
    },
    summary:
      'Bookmark a single user prompt (a thread_blocks row with type="user") for the authenticated user. Idempotent.',
  },
  removePromptFavorite: {
    method: 'DELETE',
    path: '/thread-blocks/:id/favorite',
    pathParams: z.object({
      id: z.string().min(1).max(64),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: togglePromptFavoriteResponseSchema,
      401: errorSchema,
      404: errorSchema,
    },
    summary: 'Remove a prompt bookmark for the authenticated user (no-op if absent).',
  },
  listFavoritePrompts: {
    method: 'GET',
    path: '/favorites/prompts',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    query: listFavoritePromptsQuerySchema,
    responses: {
      200: favoritePromptListResponseSchema,
      401: errorSchema,
    },
    summary:
      "List the user's favorited prompts, newest first. Prompts whose parent thread the viewer can no longer see (visibility downgraded) are filtered out at read time.",
  },
  listFavoritedBlockIdsInThread: {
    method: 'GET',
    path: '/threads/:id/favorited-blocks',
    pathParams: z.object({
      id: z.string().min(1).max(64),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: favoritedBlockIdsResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Returns the set of thread_block ids the authenticated viewer has favorited within a single thread, used to bootstrap per-prompt star state on the SessionView.',
  },
});

export type ToggleThreadFavoriteResponse = z.infer<typeof toggleThreadFavoriteResponseSchema>;
export type ListFavoriteThreadsQuery = z.infer<typeof listFavoriteThreadsQuerySchema>;
export type TogglePromptFavoriteResponse = z.infer<typeof togglePromptFavoriteResponseSchema>;
export type ListFavoritePromptsQuery = z.infer<typeof listFavoritePromptsQuerySchema>;
export type FavoritePromptResource = z.infer<typeof favoritePromptResourceSchema>;
export type FavoritePromptListResponse = z.infer<typeof favoritePromptListResponseSchema>;
export type FavoritedBlockIdsResponse = z.infer<typeof favoritedBlockIdsResponseSchema>;
